import {useEffect, useRef} from 'react';
import {hideAppLoader} from '@/matic/lib/utils/hide-app-loader';
import {useNavigate} from 'react-router-dom';
import {useAuth as useOidcAuth} from 'react-oidc-context';
import {useAppContext} from '@/matic/providers/AppProvider';
import {env} from '@/matic/config/env';
import {LocalLoginForm} from '@/matic/features/auth/components/LocalLoginForm';

/**
 * Login page - redirects to Cognito HostedUI
 *
 * This page serves as a consistent entry point for authentication:
 * - Redirects to Cognito for sign-in
 * - Handles "already authenticated" edge case
 * - The persistent app loader in index.html covers the loading state
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const {isAuthenticated, isLoading} = useAppContext();
  const oidcAuth = useOidcAuth();
  const signinInitiated = useRef(false);
  const isLocalJwt = env.AUTH_MODE === 'local-jwt';

  useEffect(() => {
    // If already authenticated, redirect to Home.
    if (isAuthenticated) {
      navigate('/home', {replace: true});
      return;
    }

    // Password login renders its own form below; nothing to redirect to.
    if (isLocalJwt) return;

    // Initiate sign-in exactly once when ready
    if (!isLoading && !signinInitiated.current) {
      signinInitiated.current = true;

      async function initiateSignIn() {
        try {
          await oidcAuth.signinRedirect();
        } catch (error) {
          console.error('Failed to initiate sign-in:', error);
          signinInitiated.current = false;
        }
      }

      void initiateSignIn();
    }
  }, [isAuthenticated, isLoading, isLocalJwt, navigate, oidcAuth]);

  if (isLocalJwt && !isLoading && !isAuthenticated) {
    hideAppLoader();
    return <LocalLoginForm />;
  }

  // Render nothing — the persistent #app-loader overlay in index.html
  // covers the screen during the entire boot + redirect sequence.
  return null;
}
